import React, { useEffect, FC } from 'react';
import {
    StyledListSelector,
    StyledEm,
    StyledSpan,
    StyledLinkRoute,
    StyleRouteIcon,
    StyleRoute,
    StyledInfo,
    StyledInfoSpan,
    StyledMainSpan,
    StyledStoreImage,
    StyledStoreDescription,
} from './styled';
import { Image } from '~/shared/components';
import { MapMarker } from '../models/mapModel';
import { Store } from '~/lib/data-contract/salesforce';
import { isNowBetweenDates, storeWillOpen } from '~/shared/utils/date-utils';
import { useTranslation } from '~/shared/utils/translation';
import { CardStore } from './CardStore';
import { usePage } from '~/templates/pages';
import { P71StoreDetailsPage } from '~/lib';

type Props = {
    storeList: MapMarker[] | null | undefined;
    selectedPin?: any;
};

export const StoreUl: FC<Props> = ({ storeList, selectedPin }) => {
    const page = usePage() as P71StoreDetailsPage;

    const { translate } = useTranslation();
    const addIsOpenNow = (openingHours: Store['c_openingHours']) => {
        return (
            openingHours?.map((time) => ({
                ...time,
                isOpen: isNowBetweenDates(time.opens, time.closes),
                willOpenToday: storeWillOpen(time.opens),
            })) ?? []
        );
    };

    //Pin marker selected from list
    useEffect(() => {
        let el: HTMLElement | null;

        document
            .querySelectorAll('[class^="pinSelected"],[class*=" pinSelected"]')
            .forEach((el) => el.classList.remove('pinSelected'));
        if (selectedPin) {
            el = document.getElementById(selectedPin.dropPoint?._id);

            if (el) {
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            }
        }
    }, [selectedPin]);

    const storeOption = storeList?.[0] as MapMarker;

    const openingHours = storeOption?.c_openingHours
        ? addIsOpenNow(storeOption?.c_openingHours)
        : [];
    const today = openingHours[0] ?? {};
    const willOpen = today.willOpenToday;
    const openingHour = openingHours.slice(1).find((schedule) => schedule.opens && schedule.closes);
    const storeOpened = today?.isOpen;

    const linkGoogle = `https://maps.google.com/?q=${storeOption.address1},${storeOption.postalCode} ${storeOption?.city}`;
    return (
        <StyledListSelector
            id={storeOption.id}
            key={`${storeOption.id}-${storeOption.lng}-${storeOption.lat}`}
        >
            <div className="storeSummary">
                <StyledInfo>
                    <strong>{storeOption?.name || ''}</strong>
                    <StyledMainSpan>
                        {storeOption?.addressLine1 || storeOption?.address1}
                    </StyledMainSpan>
                    <StyledMainSpan>
                        {storeOption?.addressLine2 || storeOption?.address2}
                    </StyledMainSpan>
                    <StyledMainSpan>
                        {storeOption?.postalCode} {storeOption?.city}
                    </StyledMainSpan>

                    <StyledInfoSpan>
                        <StyledEm storeOpened={storeOpened}>
                            {translate(storeOpened ? 'pdp.openStore' : 'pdp.closedStore')}
                        </StyledEm>
                        <span>.</span>
                        <StyledSpan>
                            {storeOpened
                                ? translate('pdp.closesAt', {
                                      closesAt: String(today.closes),
                                  })
                                : translate('pdp.opensAt', {
                                      opensAt: String(
                                          willOpen ? today.day : openingHour?.day,
                                      )?.toLocaleLowerCase(),
                                      opensHour: String(
                                          willOpen ? today.opens : openingHour?.opens,
                                      ),
                                  })}
                        </StyledSpan>
                    </StyledInfoSpan>
                </StyledInfo>
                <StyleRoute>
                    <StyledLinkRoute
                        href={linkGoogle}
                        title={translate('pdp.openStore')}
                        target="_blank"
                    >
                        <StyleRouteIcon />
                        {translate('findStore.route')}
                    </StyledLinkRoute>
                </StyleRoute>
            </div>
            <>
                {page?.image && (
                    <StyledStoreImage>
                        <Image {...page?.image} layout="intrinsic" alt={page?.imageAlt} />
                    </StyledStoreImage>
                )}

                <CardStore storeInfo={storeOption} />
                {page?.description && (
                    <StyledStoreDescription>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: page?.description,
                            }}
                        />
                    </StyledStoreDescription>
                )}
            </>
        </StyledListSelector>
    );
};
